export type DomainErrorCode =
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'CONFLICT'
  | 'BAD_REQUEST'
  | 'SERVICE_UNAVAILABLE'
  | 'INTERNAL_SERVER_ERROR'

export class DomainError extends Error {
  constructor(
    message: string,
    public code: DomainErrorCode,
  ) {
    super(message)
    this.name = this.constructor.name
  }
}

export class NotFoundError extends DomainError {
  constructor(message: string) {
    super(message, 'NOT_FOUND')
  }
}

export class ForbiddenError extends DomainError {
  constructor(message: string) {
    super(message, 'FORBIDDEN')
  }
}

export class ConflictError extends DomainError {
  constructor(message: string) {
    super(message, 'CONFLICT')
  }
}

export class ValidationError extends DomainError {
  constructor(message: string) {
    super(message, 'BAD_REQUEST')
  }
}

/**
 * A third party failed and the caller is told WE did.
 *
 * Prefer {@link UpstreamError} for anything retryable — a provider 429, a
 * timeout, an upstream 503. The pointer is here as well as there deliberately:
 * an author reading this file top-down stops at the first class that matches
 * their situation, and its current call sites (Google Places, the invitation
 * mailer, and our own backup worker) are all "something we called did not
 * answer", so that is not the distinction. What separates them is whether "try
 * again in a moment" is true.
 */
export class ExternalServiceError extends DomainError {
  constructor(message: string) {
    super(message, 'INTERNAL_SERVER_ERROR')
  }
}

/**
 * Something we depend on and do not control did not answer usefully.
 *
 * **The distinction from `ExternalServiceError` is what the caller is told.**
 * This says "try again in a moment", which is true of a provider 429, a 5xx or
 * a timeout; `INTERNAL_SERVER_ERROR` says "we broke", which is not. Reach for
 * it where repeating the call is the honest advice and the failure is already
 * counted where it happened — not as a way to quieten a fault that IS ours.
 *
 * **It is not what silences `errorMapper`'s level-50 line**, though it is easy
 * to read it that way. That mapper re-throws inside its `instanceof DomainError`
 * branch, before the logging branch, so NO domain error has ever reached it —
 * `ExternalServiceError` included. Throwing a `DomainError` at all is the lever;
 * the code decides only what the caller is told.
 */
export class UpstreamError extends DomainError {
  constructor(message: string) {
    super(message, 'SERVICE_UNAVAILABLE')
  }
}

/**
 * The discriminator a `CONFLICT` envelope carries in the tRPC error `data`,
 * declared here because both ends of the wire read it.
 *
 * `CONFLICT` is the honest code for a Postgres deadlock (`40P01`): the loser's
 * transaction wrote nothing and the caller can simply act again. It is also the
 * code several domain refusals use, and two client sites branch on the code
 * ALONE — the order flow's email gate reads it as "this email already has an
 * account", the native auth bridge as "someone else parked this flow". Neither
 * procedure touches an Order Event, so the envelope is not reachable there
 * today; what makes that unsatisfying is that it is a fact about which
 * PROCEDURES can deadlock, and nothing keeps it true.
 *
 * So the envelope names itself. A branch that means "my own conflict" excludes
 * this kind rather than assuming the code is enough.
 *
 * **The shape is a denylist, and that is a limitation rather than a design.**
 * The right end state is every conflict naming its own kind, at which point a
 * client matches positively and the exclusion is unnecessary. The kinds that
 * exist are the ones a client had to tell apart.
 */
export const DB_DEADLOCK_CONFLICT = 'db_deadlock'

/**
 * A charge for this order may already have gone through — with
 * {@link EARLIER_ORDER_PAID_CONFLICT}, the conflicts a customer's order sheet
 * answers with its own banner rather than the refusal line, because what the customer needs is to look at their orders before
 * paying as a new order. Pressing Pay again stays safe: it is the same attempt.
 */
export const CHARGE_MAYBE_TAKEN_CONFLICT = 'charge_maybe_taken'

/**
 * The sibling of {@link CHARGE_MAYBE_TAKEN_CONFLICT} for the one case where the
 * server KNOWS a payment went through: an earlier tap of this attempt paid for
 * the order before the customer changed it, and that order has just been
 * written. The sheet treats it exactly as maybe-taken — same attempt kept, same
 * notice — and only the sentence differs, because "may have gone through" sends
 * the customer to look for a payment the server knows went through, for an
 * order that differs from the one on screen (or has since been cancelled).
 */
export const EARLIER_ORDER_PAID_CONFLICT = 'earlier_order_paid'

/**
 * A Scan & Go claim was lost after the order write began — somebody else paid
 * for a scanned pallet in the seconds between the pre-charge check and the
 * insert — and the money question is SETTLED: either nothing was taken, or what
 * was taken has been refunded in full and Stripe said `succeeded`.
 *
 * Its own kind rather than {@link CHARGE_MAYBE_TAKEN_CONFLICT} because the two
 * ask the customer for opposite things. Maybe-taken says "go and look at your
 * orders before paying again" and offers a second payment; this one says the
 * pallet is gone and the money is not, so the next step is to put it back and
 * rescan — which starts a fresh charge attempt of its own. Offering "Pay as a
 * new order" here charges a customer a second time for pallets that are not
 * on the shelf.
 *
 * ONE kind for both money outcomes, because the kind says what the CLIENT does
 * and the client does the same thing either way; the sentence, which differs,
 * comes from the `scan_go` catalogue. A refund that did NOT settle is not this
 * kind — that is genuinely uncertain and keeps {@link CHARGE_MAYBE_TAKEN_CONFLICT}.
 */
export const SCAN_GO_CLAIM_LOST_CONFLICT = 'scan_go_claim_lost'

/**
 * The price a pallet's buy sheet showed is not its price, so nothing was
 * charged. Its own kind because the sheet must stop offering the old figure:
 * Pay again would only be refused again.
 */
export const PALLET_PRICE_CHANGED_CONFLICT = 'pallet_price_changed'

/** The kinds a `CONFLICT` envelope may name — see {@link DB_DEADLOCK_CONFLICT}. */
export type ConflictKind =
  | typeof DB_DEADLOCK_CONFLICT
  | typeof CHARGE_MAYBE_TAKEN_CONFLICT
  | typeof EARLIER_ORDER_PAID_CONFLICT
  | typeof SCAN_GO_CLAIM_LOST_CONFLICT
  | typeof PALLET_PRICE_CHANGED_CONFLICT

/**
 * A `ConflictError` that names its kind on the wire. The deadlock kind is not
 * thrown this way — it is read off the database error by the error shape.
 */
export class KindedConflictError extends ConflictError {
  readonly conflictKind: ConflictKind

  constructor(message: string, conflictKind: ConflictKind) {
    super(message)
    this.conflictKind = conflictKind
  }
}

/**
 * The discriminator a `BAD_REQUEST` carries in the tRPC error `data` when a
 * client has to act on WHICH refusal it is.
 *
 * A referral code's refusal is the one that needs it. The checkout previews
 * answer with it, and they also answer `BAD_REQUEST` for input that fails
 * validation or a basket that cannot be priced. The order sheet takes an
 * applied code off for the first and must not for the others — removing a code
 * nobody refused charges the customer more — and the refusal's sentence is
 * Ops-editable, so it cannot be matched on. Positive, unlike `ConflictKind`: a
 * client matches the kind it acts on. Declared here, like
 * {@link DB_DEADLOCK_CONFLICT}, because both ends of the wire read it.
 */
export const REFERRAL_CODE_REFUSAL = 'referral_code'

/** The kinds a `BAD_REQUEST` envelope may name — see {@link REFERRAL_CODE_REFUSAL}. */
export type RefusalKind = typeof REFERRAL_CODE_REFUSAL

/**
 * A `ValidationError` that names its refusal kind on the wire — see
 * {@link RefusalKind}. One class for every kind a `BAD_REQUEST` may carry, so a
 * second kind is a value added to `RefusalKind`, not another class.
 */
export class RefusalError extends ValidationError {
  readonly refusalKind: RefusalKind

  constructor(message: string, refusalKind: RefusalKind) {
    super(message)
    this.refusalKind = refusalKind
  }
}
